import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Warning, Trophy, Certificate, Printer } from "@phosphor-icons/react";

export default function AssessmentResult() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { user, isTrainingAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [training, setTraining] = useState(null);
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/attempts/${attemptId}`, { withCredentials: true });
      setData(res.data);
      // Load training info
      const t = await axios.get(`${API}/training/${res.data.attempt.training_id}`, { withCredentials: true });
      setTraining(t.data);
      // Load certificate (if any)
      if (res.data.attempt.result === "passed") {
        try {
          const certs = await axios.get(`${API}/my/certificates`, { withCredentials: true });
          const matching = certs.data.find((c) => c.training_id === res.data.attempt.training_id);
          if (matching) setCert(matching);
        } catch (e) { console.debug("ignored error:", e); }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load result");
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => { load(); }, [load]);

  const printCert = () => {
    if (!cert || !training) return;
    const LOGO_URL = "https://customer-assets.emergentagent.com/job_role-flow-hub/artifacts/rlqwkxc4_logo.png";
    const html = `
<!doctype html><html><head><title>Certificate — ${training.title}</title>
<style>
  body{font-family:Georgia,serif;margin:0;padding:60px;background:#f8f5e8}
  .cert{max-width:900px;margin:0 auto;background:#fff;padding:60px;border:8px double #1c4587;text-align:center}
  .logo{height:90px;margin:0 auto 24px;display:block;object-fit:contain}
  h1{font-size:48px;color:#1c4587;margin:0 0 10px}
  .sub{color:#555;font-size:18px;margin-bottom:40px}
  .name{font-size:44px;font-style:italic;color:#000;margin:20px 0;border-bottom:2px solid #1c4587;display:inline-block;padding:0 40px 10px}
  .body{font-size:18px;line-height:1.6;color:#333;margin:30px 0}
  .meta{display:flex;justify-content:space-around;margin-top:60px;color:#555;font-size:13px}
  .meta div{text-align:center}
  .label{font-size:11px;text-transform:uppercase;color:#999;letter-spacing:1px}
</style></head><body>
  <div class="cert">
    <img class="logo" src="${LOGO_URL}" alt="CME" crossorigin="anonymous" />
    <h1>Certificate of Completion</h1>
    <p class="sub">This is to certify that</p>
    <div class="name">${esc(user?.name || "")}</div>
    <p class="body">has successfully completed the training programme<br><strong>${esc(training.title)}</strong><br>with a score of <strong>${esc(cert.score)}%</strong></p>
    <div class="meta">
      <div><div class="label">Issued</div>${esc(cert.issued_date.split("T")[0])}</div>
      <div><div class="label">Valid Until</div>${esc(cert.expiry_date.split("T")[0])}</div>
      <div><div class="label">Certificate ID</div>${esc(cert.certificate_id)}</div>
    </div>
  </div>
  <script>
    window.addEventListener('load', function(){
      var img = document.querySelector('.logo');
      if (img.complete) { setTimeout(()=>window.print(), 300); }
      else { img.addEventListener('load', ()=>setTimeout(()=>window.print(), 300)); img.addEventListener('error', ()=>setTimeout(()=>window.print(), 300)); }
    });
  </script>
</body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-secondary"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const att = data.attempt;
  const passed = att.result === "passed";

  return (
    <div className="min-h-screen bg-background" data-testid="assessment-result">
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <Button variant="ghost" size="sm" onClick={() => navigate("/training")}><ArrowLeft size={18} className="mr-2" />Back to Trainings</Button>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
        <Card className={passed ? "border-green-200 bg-green-50/40" : "border-red-200 bg-red-50/40"}>
          <CardContent className="pt-6 text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${passed ? "bg-green-100" : "bg-red-100"}`}>
              {passed ? <Trophy size={40} weight="bold" className="text-green-600" /> : <Warning size={40} weight="bold" className="text-red-600" />}
            </div>
            <h2 className="text-3xl font-bold mb-2">{passed ? "Congratulations!" : "Not Passed"}</h2>
            <p className="text-muted-foreground mb-4">{training?.title}</p>
            <div className="text-6xl font-black mb-2">{att.score}%</div>
            <p className="text-sm text-muted-foreground">Passing score: {training?.passing_score}%</p>
            <Badge className={`mt-4 ${passed ? "bg-green-600" : "bg-red-600"} text-white`}>{att.result?.toUpperCase()}</Badge>

            {cert && (
              <div className="mt-6 flex gap-2 justify-center">
                <Button onClick={printCert} data-testid="print-certificate-btn">
                  <Certificate size={16} className="mr-2" />Download Certificate
                </Button>
                <Button variant="outline" onClick={printCert}>
                  <Printer size={16} className="mr-2" />Print
                </Button>
              </div>
            )}
            {!passed && training?.allow_retakes && (
              <Button className="mt-4" onClick={() => navigate(`/training/${training.training_id}/take`)}>
                Retake Assessment
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Per-question breakdown — admins only. Employees should not see what they got right/wrong (forces real learning before retake). */}
        {isTrainingAdmin && data.answers && data.answers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Answer Review</CardTitle>
            <CardDescription>Per-question breakdown (admin view)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.answers.map((a, i) => {
              const q = a.question || {};
              const correct = a.is_correct === true || (a.ai_score != null && a.ai_score >= 0.7);
              return (
                <div key={a.answer_id} className={`p-3 border-l-4 rounded ${correct ? "border-green-500 bg-green-50/30" : "border-red-400 bg-red-50/30"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-sm flex-1">{i + 1}. {q.question_text}</p>
                    {a.is_correct != null ? (
                      <Badge variant={a.is_correct ? "default" : "destructive"} className="text-xs">{a.is_correct ? <CheckCircle size={12} className="mr-1" /> : <Warning size={12} className="mr-1" />}{a.is_correct ? "Correct" : "Wrong"}</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">AI {Math.round((a.ai_score || 0) * 100)}%</Badge>
                    )}
                  </div>
                  <div className="mt-2 text-xs space-y-1">
                    <p><span className="text-muted-foreground">Your answer:</span> {a.employee_answer || <em>(no answer)</em>}</p>
                    {q.correct_answer && <p><span className="text-muted-foreground">Correct answer:</span> {q.correct_answer}</p>}
                    {q.expected_answer && <p><span className="text-muted-foreground">Expected:</span> {q.expected_answer}</p>}
                    {a.ai_feedback && <p className="text-muted-foreground italic">{a.ai_feedback}</p>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
        )}

        {/* Employee learning prompt */}
        {!isTrainingAdmin && !passed && (
          <Card className="border-amber-200 bg-amber-50/40">
            <CardContent className="pt-6 text-center text-sm text-amber-900">
              <p className="font-semibold mb-1">Want to retake?</p>
              <p>Re-watch the training and try again. Your retake will use a fresh set of questions.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
